import type { Feature, WorkProduct } from "../types";
import { featureStudioReturnUrl } from "./appViewRoutes";
import { editorDeepLinkUrl } from "./editorDeepLink";
import { resolveWorkProductEditorScope } from "./workProductEditorScope";

export interface WorkProductNavigationInput {
    projectId: number;
    workProduct: WorkProduct;
    selectedFeature?: Feature | null;
}

export interface WorkProductNavigationUrls {
    editorUrl: string;
    featureStudioUrl: string;
}

export interface NavigationProjectIds {
    selectedProjectId?: unknown;
    currentProjectId?: unknown;
    authProjectId?: unknown;
}

interface EditorHistoryState {
    view: string;
    productStudioEditorEntry: true;
    returnRoute: string;
}

function canonicalRoute(route: string): string {
    const parsed = new URL(route, "https://product-studio.invalid");
    parsed.searchParams.sort();
    const search = parsed.searchParams.toString();
    return `${parsed.pathname}${search ? `?${search}` : ""}`;
}

const normalizeLevel = (value: unknown): string =>
    String(value || "")
        .replace(/[_\s-]/g, "")
        .toUpperCase();

export function resolveNavigationProjectId({ selectedProjectId, currentProjectId, authProjectId }: NavigationProjectIds): number | null {
    for (const candidate of [selectedProjectId, currentProjectId, authProjectId]) {
        const productId = Number(candidate);
        if (Number.isSafeInteger(productId) && productId > 0) return productId;
    }
    return null;
}

export function editorHistoryState(view: string, returnRoute: string): EditorHistoryState {
    return {
        view,
        productStudioEditorEntry: true,
        returnRoute: canonicalRoute(returnRoute)
    };
}

export function shouldUseBrowserBackForEditor(state: unknown, desiredReturnRoute: string): boolean {
    if (!state || typeof state !== "object") return false;
    const candidate = state as Partial<EditorHistoryState>;
    return candidate.productStudioEditorEntry === true && candidate.returnRoute === canonicalRoute(desiredReturnRoute);
}

export function workProductNavigationUrls({ projectId, workProduct, selectedFeature }: WorkProductNavigationInput): WorkProductNavigationUrls | null {
    const workProductId = Number(workProduct.id);
    if (!Number.isSafeInteger(projectId) || projectId <= 0 || !Number.isSafeInteger(workProductId) || workProductId <= 0) {
        return null;
    }

    const scope = resolveWorkProductEditorScope(workProduct, selectedFeature);
    const level = normalizeLevel(workProduct.level);
    if (level === "FEATURE" && scope.featureId === undefined) return null;
    if (level === "USERSTORY" && (scope.featureId === undefined || !scope.userStoryId)) {
        return null;
    }

    const editorUrl = editorDeepLinkUrl({
        projectId,
        workProductId,
        ...(scope.featureId !== undefined ? { featureId: scope.featureId } : {}),
        ...(scope.userStoryId ? { storyId: scope.userStoryId } : {})
    });

    if (scope.userStoryId) {
        return {
            editorUrl,
            featureStudioUrl: featureStudioReturnUrl({
                projectId,
                level: "USERSTORY",
                nodeId: scope.userStoryId
            })
        };
    }
    if (scope.featureId !== undefined) {
        return {
            editorUrl,
            featureStudioUrl: featureStudioReturnUrl({
                projectId,
                level: "FEATURE",
                nodeId: String(scope.featureId)
            })
        };
    }
    return {
        editorUrl,
        featureStudioUrl: featureStudioReturnUrl({
            projectId,
            level: "PRODUCT"
        })
    };
}
